import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";
import RegistrationDialog from "../components/RegistrationDialog";
import { MagnifyingGlass, CaretLeft, CaretRight } from "@phosphor-icons/react";

const TABS = [
  { key: "approved", label: "Approved", color: "var(--sms-primary)" },
  { key: "enrolled", label: "Enrolled", color: "var(--sms-success)" },
];

const PAGE_SIZE = 24;

export default function ApprovedStudents() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("approved");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(0); }, 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page when tab changes
  useEffect(() => { setPage(0); }, [tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: tab, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (debounced) params.q = debounced;
      const [list, count] = await Promise.all([
        api.get("/registrations", { params }),
        api.get("/registrations/count", { params: { status: tab, q: debounced || undefined } }),
      ]);
      setItems(list.data);
      setTotal(count.data?.total || 0);
    } finally {
      setLoading(false);
    }
  }, [tab, page, debounced]);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="font-mono-sms text-xs uppercase tracking-[0.3em] text-[var(--sms-success)] mb-3">Roster</div>
        <h1 className="font-display text-5xl font-black tracking-tighter">Approved &amp; Enrolled.</h1>
        <p className="text-slate-500 mt-2">
          {total} {tab} student{total === 1 ? "" : "s"}
          {debounced && <span className="font-mono-sms text-xs ml-2 text-[var(--sms-primary)]">· filtered "{debounced}"</span>}
        </p>
      </div>

      <div className="flex items-center gap-1 mb-6 border-b border-slate-200" data-testid="roster-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-5 py-3 font-mono-sms text-xs uppercase tracking-[0.2em] -mb-px border-b-2 transition-colors ${
              tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4" data-testid="roster-search-bar">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="input-roster-search"
            placeholder="Search by name, student id, phone, email…"
            className="sms-input pl-9"
          />
        </div>
        {search && (
          <button onClick={() => setSearch("")} data-testid="clear-roster-search" className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900">
            Clear
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="approved-grid">
        {loading && (
          <div className="col-span-full text-center text-slate-400 py-20 font-mono-sms text-xs uppercase tracking-[0.2em]">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center text-slate-400 py-20 border border-dashed border-slate-200">
            No {tab} students yet{debounced ? " matching that search" : ""}.
          </div>
        )}
        {items.map((r) => (
          <button
            key={r.id}
            data-testid={`approved-card-${r.student_id}`}
            onClick={() => setSelected(r)}
            className="sms-card sms-card-hoverable text-left p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-slate-100 border border-slate-200 overflow-hidden shrink-0">
                {r.photo_base64 ? (
                  <img src={r.photo_base64} alt={r.full_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-mono-sms text-xs text-slate-400">
                    No photo
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold tracking-tight truncate">{r.full_name}</div>
                <div className="text-xs text-slate-500 truncate">{r.phone}</div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="font-mono-sms text-xs text-[var(--sms-primary)] inline-block px-2 py-0.5 bg-[var(--sms-primary)]/5 border border-[var(--sms-primary)]/20">
                    {r.student_id}
                  </div>
                  {r.status === "enrolled" && (
                    <span className="sms-badge sms-badge-approved">Enrolled</span>
                  )}
                  {r.payment_status === "claimed" && r.status !== "enrolled" && (
                    <span className="sms-badge sms-badge-pending">Pay claimed</span>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between font-mono-sms text-[10px] uppercase tracking-[0.15em] text-slate-500">
              <span>{r.course_name || "—"}</span>
              <span>{new Date(r.enrolled_at || r.decided_at || r.submitted_at).toLocaleDateString()}</span>
            </div>
          </button>
        ))}
      </div>

      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between" data-testid="roster-pagination">
          <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="prev-roster-page"
              className="sms-btn-secondary inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div className="font-mono-sms text-xs tracking-[0.2em]" data-testid="roster-page-indicator">
              {page + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
            </div>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= total}
              data-testid="next-roster-page"
              className="sms-btn-secondary inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {selected && (
        <RegistrationDialog
          registration={selected}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </AdminLayout>
  );
}
